import { useInfiniteQuery, useQueries } from '@zg-rentals/react-query5';

import { RentalManagerAccountViewZrmV2WebApiHandler_units } from '@zg-rentals/ts-rental-manager-api-beta-client';

import PropertiesConstants from '../../constants/PropertiesConstants';

import type { FilterState } from '../../containers/ZRAMPropertiesPage';
import type { AccountViewMultiplexUnitsResponseDto } from '@zg-rentals/ts-rental-manager-api-beta-client';

export type MultiplexUnitsFlattenedData = {
  units: AccountViewMultiplexUnitsResponseDto['units'];
  listingCount: AccountViewMultiplexUnitsResponseDto['listingCount'];
  pager: AccountViewMultiplexUnitsResponseDto['pager'];
};

const transformFiltersToUnitsParams = (filters: FilterState, groupId: string, startKeyExclusive: string) => ({
  groupId,
  startKeyExclusive: startKeyExclusive || undefined,
  includeActive:
    filters.subNavFilterType === PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.ON_MARKET ||
    filters.subNavFilterType === PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.ARCHIVED ||
    filters.subNavFilterType === PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.ALL,
  includeInactive:
    filters.subNavFilterType === PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.OFF_MARKET ||
    filters.subNavFilterType === PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.ARCHIVED ||
    filters.subNavFilterType === PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.ALL,
  includeArchived: filters.subNavFilterType === PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.ARCHIVED,
  includeUnarchived: filters.subNavFilterType !== PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.ARCHIVED,
  searchText: filters.searchText,
  limit: PropertiesConstants.PROPERTIES_LIMIT_DEFAULT,
});

export type UseGetMultiplexUnitsDataOptions = {
  enabled?: boolean;
};

export const useGetMultiplexUnitsData = (
  filters: FilterState,
  groupId: string,
  options: UseGetMultiplexUnitsDataOptions = {},
) => {
  const { enabled = true } = options;

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isPending, isError, isFetching } = useInfiniteQuery<
    AccountViewMultiplexUnitsResponseDto,
    Error,
    { pages: Array<AccountViewMultiplexUnitsResponseDto> },
    Array<string | FilterState>,
    string
  >({
    queryKey: ['getMultiplexUnits', filters, groupId],
    queryFn: async ({ pageParam }) => {
      return RentalManagerAccountViewZrmV2WebApiHandler_units(
        transformFiltersToUnitsParams(filters, groupId, pageParam),
      );
    },
    initialPageParam: '',
    getNextPageParam: (lastPage) => (lastPage.pager?.hasMore ? lastPage.pager.endKey : undefined),
    enabled: enabled && !!groupId,
    // Keep previous data during refetch to prevent UI flicker
    placeholderData: (previousData) => {
      if (!previousData?.pages?.length) {
        return;
      }

      return previousData;
    },
  });

  const multiplexData = data?.pages.length
    ? {
        units: data.pages.flatMap((page) => page.units),
        listingCount: data.pages[0].listingCount,
        pager: data.pages[data.pages.length - 1].pager,
      }
    : {};

  return {
    data: multiplexData,
    isPending,
    isFetching,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  };
};

/** Fetches units for multiple group IDs in parallel. Use when multiple groups can be expanded at once. */
export const useGetMultiplexUnitsDataForGroups = (filters: FilterState, groupIds: Array<string>) => {
  const results = useQueries({
    queries: groupIds.map((groupId) => ({
      queryKey: ['getMultiplexUnits', filters, groupId],
      queryFn: async () => {
        const firstPage = await RentalManagerAccountViewZrmV2WebApiHandler_units(
          transformFiltersToUnitsParams(filters, groupId, ''),
        );
        return firstPage;
      },
      enabled: !!groupId,
    })),
  });

  const dataByGroupId: Record<string, MultiplexUnitsFlattenedData> = {};
  results.forEach((result, i) => {
    const groupId = groupIds[i];
    if (!groupId || !result.data) return;
    const data = result.data;
    dataByGroupId[groupId] = {
      units: data.units,
      listingCount: data.listingCount,
      pager: data.pager,
    };
  });

  return {
    dataByGroupId,
    results,
    isPending: results.some((r) => r.isPending),
    isFetching: results.some((r) => r.isFetching),
  };
};
